import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSubflowTemplates,
  getSubflowTemplate,
  createSubflowTemplate,
  editSubflowTemplate,
  deleteSubflowTemplate,
} from '@/api/subflowTemplates';
import type {
  CreateSubflowTemplateRequestDto,
  EditSubflowTemplateRequestDto,
  SubflowTemplateResponseDto,
  SubflowTemplateType,
} from '@/types/api/subflowTemplate';

export const SUBFLOW_TEMPLATES_QUERY_KEY = 'subflowTemplates';

export function usePrefetchSubflowTemplates() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.prefetchQuery({
      queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list', undefined],
      queryFn: () => listSubflowTemplates(),
    });
  }, [queryClient]);
}

export const useListSubflowTemplates = (type?: SubflowTemplateType, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list', type],
    queryFn: () => listSubflowTemplates(type),
    ...options,
  });
};

export const useGetSubflowTemplate = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'detail', id],
    queryFn: () => getSubflowTemplate(id),
    enabled: !!id && options?.enabled !== false,
  });
};

export function useCreateSubflowTemplate() {
  const qc = useQueryClient();

  return useMutation<SubflowTemplateResponseDto, Error, CreateSubflowTemplateRequestDto>({
    mutationFn: createSubflowTemplate,
    onSuccess: (data) => {
      qc.setQueryData<SubflowTemplateResponseDto[]>([SUBFLOW_TEMPLATES_QUERY_KEY, 'list', data.type], (old) => {
        const prev = old ?? [];
        if (prev.some((i) => i.id === data.id)) return prev;
        return [...prev, data];
      });
      void qc.invalidateQueries({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list'] });
    },
  });
}

export function useEditSubflowTemplate() {
  const qc = useQueryClient();

  return useMutation<SubflowTemplateResponseDto, Error, { id: string; dto: EditSubflowTemplateRequestDto }>({
    mutationFn: ({ id, dto }) => editSubflowTemplate(id, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list'] });
    },
  });
}

export function useDeleteSubflowTemplate() {
  const qc = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteSubflowTemplate,
    onSuccess: (_data, id) => {
      qc.setQueriesData<SubflowTemplateResponseDto[]>({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list'] }, (old) =>
        (old ?? []).filter((item) => item.id !== id),
      );
      qc.removeQueries({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'detail', id] });
    },
  });
}
